/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
}

let resolveCb: ((value: boolean) => void) | null = null;
const listeners = new Set<(opts: ConfirmOptions | null) => void>();
let currentOpts: ConfirmOptions | null = null;

function notify() {
  listeners.forEach((l) => l(currentOpts ? { ...currentOpts } : null));
}

export function confirmModal(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    // A second confirm replacing a pending one used to drop the first
    // `resolve` on the floor, leaving whoever awaited it — always a
    // `requestClose` — suspended for the life of the page. Settle it as
    // "cancelled" so that caller unwinds.
    resolveCb?.(false);
    currentOpts = opts;
    resolveCb = resolve;
    notify();
  });
}

export function ConfirmModal() {
  // Seeded from the module state rather than `null`: a dialog requested while
  // this host was remounting (a route change, a layout re-render) would
  // otherwise never be shown, and its promise would never settle.
  const [opts, setOpts] = useState<ConfirmOptions | null>(currentOpts);

  useEffect(() => {
    const cb = (o: ConfirmOptions | null) => setOpts(o);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  if (!opts) return null;

  const handleConfirm = () => {
    currentOpts = null;
    notify();
    resolveCb?.(true);
    resolveCb = null;
  };

  const handleCancel = () => {
    currentOpts = null;
    notify();
    resolveCb?.(false);
    resolveCb = null;
  };

  const confirmClass = opts.variant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-agrida-700 hover:bg-agrida-800 text-white';

  return createPortal(
    // Rendered into <body>, deliberately: z-index only orders siblings within
    // the same stacking context, so while this lived inside PrivateLayout's tree
    // its z-[98] was no guarantee of sitting above a page-level sheet — and it
    // did not. What the tester saw was the exit warning painting *behind* the
    // still-open BottomSheet: tapping X or the backdrop appeared to do nothing,
    // the sheet could only be left with the browser's back button, and the
    // warning became visible only once the sheet was gone. A portal to <body>
    // takes the layer out of every wrapper that could trap it.
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={handleCancel} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] animate-modal-slide-up">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">{opts.title}</h3>
          <button
            type="button"
            onClick={handleCancel}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-6 whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto">{opts.message}</p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            {opts.cancelText || 'Hủy'}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] ${confirmClass}`}
          >
            {opts.confirmText || 'Xác nhận'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
